import React, { memo, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import {
  sheetDisplayName,
  sheetIndexingLabel,
  sheetIndexingTone,
} from './sheetPanelUtils.js';

const List = styled.div`
  flex: 0 0 auto;
  max-height: 36%;
  min-height: 0;
  overflow-y: auto;
  padding: 6px 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
`;

const Item = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  text-align: left;
  padding: 6px 8px;
  border-radius: 8px;
  margin-bottom: 4px;
  border: 1px solid ${(p) => (p.$active ? '#e97024' : 'transparent')};
  background: ${(p) => (p.$active ? 'rgba(233, 112, 36, 0.08)' : 'rgba(255, 255, 255, 0.92)')};
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s;

  &:hover {
    background: rgba(255, 107, 53, 0.08);
  }
`;

const StatusDot = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  flex-shrink: 0;
  display: grid;
  place-items: center;
  font-size: 10px;
  font-weight: 700;
  color: #fff;
  background: ${(p) => {
    if (p.$tone === 'progress') return '#e97024';
    if (p.$tone === 'warn') return '#f59e0b';
    return '#10b981';
  }};
`;

const ItemBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.div`
  font-weight: 600;
  font-size: 12px;
  color: #1f2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Meta = styled.div`
  font-size: 10px;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Chip = styled.span`
  display: inline-block;
  margin-left: 4px;
  padding: 0 5px;
  border-radius: 4px;
  font-size: 9px;
  font-weight: 600;
  background: rgba(0, 0, 0, 0.06);
  color: #4b5563;
`;

const Search = styled.input`
  width: 100%;
  height: 28px;
  padding: 0 8px;
  margin-bottom: 6px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 11px;
`;

const SheetRow = memo(function SheetRow({ sheet, active, onSelect, rowRef }) {
  const tone = sheetIndexingTone(sheet);
  const label = sheetIndexingLabel(sheet);
  const count = sheet.componentCount || sheet.ir?.components?.length || 0;

  return (
    <Item
      ref={rowRef}
      type="button"
      $active={active}
      onClick={() => onSelect(sheet)}
    >
      <StatusDot $tone={tone}>
        {tone === 'progress' ? '…' : tone === 'warn' ? '?' : '✓'}
      </StatusDot>
      <ItemBody>
        <Title title={sheet.sheetId || sheetDisplayName(sheet)}>
          {sheet.sheetId || sheetDisplayName(sheet)}
        </Title>
        <Meta>
          <Chip>{label}</Chip>
          {count > 0 && <Chip>{count} labels</Chip>}
        </Meta>
      </ItemBody>
    </Item>
  );
});

export default function SheetListPanel({
  sheets,
  sheetsLoading,
  activeIrKey,
  search,
  onSearchChange,
  onSelectSheet,
  hideWhenSingle = false,
}) {
  const activeRowRef = useRef(null);

  const filtered = useMemo(() => {
    const q = (search || '').trim().toLowerCase();
    if (!q) return sheets;
    return sheets.filter((s) => {
      const name = sheetDisplayName(s).toLowerCase();
      const id = (s.sheetId || '').toLowerCase();
      return name.includes(q) || id.includes(q);
    });
  }, [sheets, search]);

  useEffect(() => {
    if (activeRowRef.current) {
      activeRowRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [activeIrKey]);

  if (sheetsLoading && sheets.length === 0) {
    return (
      <List>
        <Meta style={{ padding: 4 }}>Loading sheets…</Meta>
      </List>
    );
  }

  if (hideWhenSingle && sheets.length <= 1 && !(search || '').trim()) {
    return null;
  }

  const showSearch = sheets.length > 3 || (search || '').trim();

  return (
    <List>
      {showSearch && (
        <Search
          placeholder="Filter sheets…"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      )}
      {filtered.map((sheet) => (
        <SheetRow
          key={sheet.irKey}
          sheet={sheet}
          active={activeIrKey === sheet.irKey}
          rowRef={activeIrKey === sheet.irKey ? activeRowRef : null}
          onSelect={onSelectSheet}
        />
      ))}
      {!sheetsLoading && filtered.length === 0 && (
        <Meta style={{ padding: 4 }}>No sheets match.</Meta>
      )}
    </List>
  );
}
